import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const VersionHistory = ({ document, onClose }) => {
  const [selectedVersions, setSelectedVersions] = useState([]);

  const mockVersions = [
    {
      id: 1,
      version: '3.0',
      date: '2024-01-15',
      time: '14:30',
      author: 'Sarah Wanjiku',
      size: '2.4 MB',
      changes: 'Updated client information and added new clauses',
      status: 'Current',
      downloadUrl: '#'
    },
    {
      id: 2,
      version: '2.1',
      date: '2024-01-14',
      time: '09:15',
      author: 'John Kiprotich',
      size: '2.3 MB',
      changes: 'Minor formatting corrections and typo fixes',
      status: 'Previous',
      downloadUrl: '#'
    },
    {
      id: 3,
      version: '2.0',
      date: '2024-01-13',
      time: '16:45',
      author: 'Mary Achieng',
      size: '2.2 MB',
      changes: 'Major revision with legal review comments incorporated',
      status: 'Previous',
      downloadUrl: '#'
    },
    {
      id: 4,
      version: '1.2',
      date: '2024-01-12',
      time: '11:20',
      author: 'David Mutua',
      size: '2.1 MB',
      changes: 'Added signatures and witness information',
      status: 'Previous',
      downloadUrl: '#'
    },
    {
      id: 5,
      version: '1.1',
      date: '2024-01-11',
      time: '13:10',
      author: 'Grace Wanjiku',
      size: '2.0 MB',
      changes: 'Initial draft with basic contract terms',
      status: 'Previous',
      downloadUrl: '#'
    },
    {
      id: 6,
      version: '1.0',
      date: '2024-01-10',
      time: '10:00',
      author: 'Peter Ochieng',
      size: '1.9 MB',
      changes: 'Document created from template',
      status: 'Original',
      downloadUrl: '#'
    }
  ];

  const handleVersionSelect = (versionId) => {
    setSelectedVersions(prev => 
      prev.includes(versionId)
        ? prev.filter(id => id !== versionId)
        : prev.length < 2 
          ? [...prev, versionId]
          : [prev[1], versionId]
    );
  };

  const handleCompareVersions = () => {
    if (selectedVersions.length === 2) {
      console.log('Comparing versions:', selectedVersions);
      // Open comparison view
    }
  };

  const handleRestoreVersion = (versionId) => {
    console.log('Restoring version:', versionId);
    // Restore version logic
  };

  const formatDateTime = (date, time) => {
    return `${new Date(date).toLocaleDateString('en-GB')} at ${time}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Current': return 'text-success bg-success-50';
      case 'Previous': return 'text-primary bg-primary-50';
      case 'Original': return 'text-secondary bg-secondary-50';
      default: return 'text-secondary bg-secondary-50';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-heading font-semibold text-text-primary">Version History</h2>
            <p className="text-sm text-text-secondary mt-1">{document.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Actions Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-sm text-text-secondary">
                {selectedVersions.length} version{selectedVersions.length !== 1 ? 's' : ''} selected
              </span>
              {selectedVersions.length === 2 && (
                <button
                  onClick={handleCompareVersions}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth"
                >
                  <Icon name="GitCompare" size={16} className="mr-2" />
                  Compare Versions
                </button>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <button className="flex items-center px-3 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-smooth">
                <Icon name="Download" size={16} className="mr-2" />
                Download All
              </button>
              <button className="flex items-center px-3 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-smooth">
                <Icon name="Archive" size={16} className="mr-2" />
                Archive Old
              </button>
            </div>
          </div>

          {/* Version Timeline */}
          <div className="space-y-4">
            {mockVersions.map((version, index) => (
              <div
                key={version.id}
                className={`relative flex items-start p-4 border rounded-lg transition-smooth ${
                  selectedVersions.includes(version.id)
                    ? 'border-primary bg-primary-50' :'border-border hover:border-primary hover:bg-primary-50'
                }`}
              >
                {/* Timeline Line */}
                {index < mockVersions.length - 1 && (
                  <div className="absolute left-8 top-16 w-0.5 h-8 bg-border"></div>
                )}

                {/* Selection Checkbox */}
                <div className="flex items-center mr-4">
                  <input
                    type="checkbox"
                    checked={selectedVersions.includes(version.id)}
                    onChange={() => handleVersionSelect(version.id)}
                    disabled={selectedVersions.length >= 2 && !selectedVersions.includes(version.id)}
                    className="rounded border-border focus:ring-primary"
                  />
                </div>

                {/* Version Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                  version.status === 'Current' ? 'bg-success text-white' : 'bg-secondary-200 text-text-secondary'
                }`}>
                  <Icon name={version.status === 'Current' ? 'CheckCircle' : 'FileText'} size={20} />
                </div>

                {/* Version Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium text-text-primary">Version {version.version}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(version.status)}`}>
                        {version.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => window.open(version.downloadUrl, '_blank')}
                        className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
                        title="Download"
                      >
                        <Icon name="Download" size={16} />
                      </button>
                      <button
                        className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
                        title="Preview"
                      >
                        <Icon name="Eye" size={16} />
                      </button>
                      {version.status !== 'Current' && (
                        <button
                          onClick={() => handleRestoreVersion(version.id)}
                          className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
                          title="Restore Version"
                        >
                          <Icon name="RotateCcw" size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-text-secondary mb-3">
                    <div className="flex items-center">
                      <Icon name="User" size={14} className="mr-2" />
                      {version.author}
                    </div>
                    <div className="flex items-center">
                      <Icon name="Clock" size={14} className="mr-2" />
                      {formatDateTime(version.date, version.time)}
                    </div>
                    <div className="flex items-center">
                      <Icon name="HardDrive" size={14} className="mr-2" />
                      {version.size}
                    </div>
                  </div>

                  <p className="text-sm text-text-primary bg-secondary-50 p-3 rounded-lg">
                    {version.changes}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Version Statistics */}
          <div className="mt-8 p-4 bg-secondary-50 rounded-lg">
            <h4 className="font-medium text-text-primary mb-3">Version Statistics</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{mockVersions.length}</div>
                <div className="text-text-secondary">Total Versions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-success">5</div>
                <div className="text-text-secondary">Contributors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">5</div>
                <div className="text-text-secondary">Days Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary">0.5 MB</div>
                <div className="text-text-secondary">Size Growth</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-text-secondary">
            {selectedVersions.length === 2 ? 'Select "Compare Versions" to see differences' : 'Select up to 2 versions to compare'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-smooth"
            >
              Close
            </button>
            {selectedVersions.length === 2 && (
              <button
                onClick={handleCompareVersions}
                className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-smooth"
              >
                Compare Selected
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionHistory;